import * as React from 'react';
import { styled } from '@linaria/react';
import * as Accordion from '@radix-ui/react-accordion';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import Paper from '@/components/Paper';
import IconChevron from '@/components/Icons/IconChevron';

const QUESTIONS = [
  {
    title: 'Example question',
    answer: 'Example answer',
  },
  {
    title: 'Click/tap to expand',
    answer:
      'This example answer is much longer, with several lines of text that meander aimlessly. These words aren’t really intended to be read, they exist purely to increase the height of the accordion so that the enter animation is more impressive.',
  },
  {
    title: 'One last question',
    answer:
      'You know the drill by now. These aren’t real questions. 😅',
  },
];

function HeightAccordionDemo() {
  const [hasApprovedMotion, setHasApprovedMotion] =
    React.useState(false);
  const [expandedQuestionTitle, setExpandedQuestionTitle] =
    React.useState<string>('');
  const prefersReducedMotion = usePrefersReducedMotion();

  const requiresApproval = prefersReducedMotion && !hasApprovedMotion;

  return (
    <Wrapper>
      <Accordion.Root
        className="AccordionRoot"
        type="single"
        collapsible
        onValueChange={(title) => {
          setExpandedQuestionTitle(title);
        }}
      >
        {QUESTIONS.map((question) => {
          const isExpanded = expandedQuestionTitle === question.title;
          return (
            <AccordionItem
              key={question.title}
              title={question.title}
              answer={question.answer}
              isExpanded={isExpanded}
              requiresApproval={requiresApproval}
              setHasApprovedMotion={setHasApprovedMotion}
            />
          );
        })}
      </Accordion.Root>
    </Wrapper>
  );
}

function AccordionItem({
  title,
  answer,
  isExpanded,
  requiresApproval,
  setHasApprovedMotion,
}: {
  title: string;
  answer: string;
  isExpanded: boolean;
  requiresApproval: boolean;
  setHasApprovedMotion: (hasApprovedMotion: boolean) => void;
}) {
  const [isHovering, setIsHovering] = React.useState(false);

  return (
    <Item key={title} value={title}>
      <Trigger
        onMouseEnter={() => {
          setIsHovering(true);
        }}
        onMouseLeave={() => {
          setIsHovering(false);
        }}
        onClick={(ev) => {
          if (requiresApproval) {
            const confirmed = confirm(
              'Your device is configured to reduce motion, and this demo includes a significant amount of motion. Would you like to continue?'
            );
            if (confirmed) {
              setHasApprovedMotion(true);
            } else {
              ev.preventDefault();
            }
          }
        }}
      >
        <Title>{title}</Title>
        <IconChevron
          direction={isExpanded ? 'up' : 'down'}
          isHovering={isHovering}
        />
      </Trigger>
      <Content>
        <InnerContent>{answer}</InnerContent>
      </Content>
    </Item>
  );
}

const Wrapper = styled(Paper)`
  margin-bottom: 64px;
  margin-inline: auto;
  width: 100%;
  max-width: 28rem;
  padding: 4px;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  --spring: linear(
    0,
    0.012 0.9%,
    0.049 2%,
    0.409 9.3%,
    0.513 11.9%,
    0.606 14.7%,
    0.691 17.9%,
    0.762 21.3%,
    0.82 25%,
    0.868 29.1%,
    0.907 33.6%,
    0.937 38.7%,
    0.976 51.3%,
    0.994 68.8%,
    1
  );

  html[data-color-mode='light'] & {
    box-shadow:
      0px 1px 2px hsl(210deg 15% 6% / 0.15),
      0px 2px 5px hsl(210deg 15% 6% / 0.1),
      0px 4px 10px hsl(210deg 15% 6% / 0.1),
      0px 8px 24px hsl(210deg 15% 6% / 0.1);
  }
`;

const Item = styled(Accordion.Item)`
  &:has([data-state='closed']):not(:last-child) {
    border-bottom: 1px solid var(--color-gray-200);
  }
`;

const Trigger = styled(Accordion.Trigger)`
  display: flex;
  width: 100%;
  padding: 1rem;
`;

const Title = styled.span`
  flex: 1;
  font-weight: var(--font-weight-medium);
`;

const Content = styled(Accordion.Content)`
  background: var(--color-gray-100);
  overflow: hidden;
  overflow: clip;
  border-radius: 4px;
  box-shadow:
    inset 0px 0.5px 1px hsl(210deg 15% 6% / 0.1),
    inset 0px 1px 3px hsl(210deg 15% 6% / 0.1),
    inset 0px 2px 6px hsl(210deg 15% 6% / 0.1);
  text-wrap: pretty;

  html[data-color-mode='light'] & {
    background: var(--color-gray-50);
  }

  &[data-state='open'] {
    animation: slideDown 450ms var(--spring);
  }
  &[data-state='closed'] {
    animation: slideUp 450ms var(--spring);
  }

  @keyframes slideDown {
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes slideUp {
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
    }
  }
`;

const InnerContent = styled.div`
  padding: 1rem;
`;

export default HeightAccordionDemo;
